/* ==========================================================================
   ps-nav.js — Peakspan H2 site-wide navigation drawer (Round 5 IA, 2026-08-09)

   Owns ONLY the mobile hamburger drawer behaviour:
     · open / close via [data-ps-navtoggle], [data-ps-navclose] and the overlay
     · aria-expanded on the toggle, aria-modal dialog semantics on the drawer
     · focus trap while open, focus restored to the toggle on close
     · ESC closes; a link click closes (so in-page anchors actually scroll)
     · scroll lock via html.ps-nav-lock (defined in ps.css — NOT the basket's
       ps-drawer-lock, which lives in ps-modules.css and is absent on index)
     · closes itself if the viewport grows past the desktop breakpoint

   aria-current="page" is written statically into the markup on every page, so
   the current-page state survives with JavaScript disabled. This file never
   touches it. No dependencies. ES5 only, matching ps-preorder.js.
   ========================================================================== */
(function () {
  'use strict';

  var DESKTOP = 1180; /* must match the @media (min-width: 1180px) in ps.css */

  var reduced = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var toggle = null;
  var drawer = null;
  var overlay = null;
  var closeBtn = null;
  var lastFocus = null;
  var closeTimer = null;
  /* explicit state: drawer.hidden lags by one animation frame on close, so
     reading it would create a dead zone where the toggle does nothing */
  var opened = false;

  function focusables() {
    if (!drawer) return [];
    var list = drawer.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var el = list[i];
      if (el.offsetWidth || el.offsetHeight || el.getClientRects().length) out.push(el);
    }
    return out;
  }

  function isOpen() {
    return opened;
  }

  function open() {
    if (!drawer || opened) return;
    opened = true;
    if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
    lastFocus = document.activeElement;
    if (overlay) overlay.hidden = false;
    drawer.hidden = false;
    /* double rAF so the slide-in transition runs from the hidden state */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if (overlay) overlay.classList.add('is-open');
        drawer.classList.add('is-open');
      });
    });
    document.documentElement.classList.add('ps-nav-lock');
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'true');
      toggle.setAttribute('aria-label', 'Close menu');
    }
    if (closeBtn) closeBtn.focus();
  }

  function close(restoreFocus) {
    if (!drawer || !opened) return;
    opened = false;
    if (overlay) overlay.classList.remove('is-open');
    drawer.classList.remove('is-open');
    document.documentElement.classList.remove('ps-nav-lock');
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-label', 'Open menu');
    }
    var delay = reduced ? 0 : 340;
    closeTimer = setTimeout(function () {
      closeTimer = null;
      if (opened) return; /* reopened mid-animation — leave it visible */
      if (overlay) overlay.hidden = true;
      drawer.hidden = true;
    }, delay);
    if (restoreFocus !== false && toggle && toggle.focus) toggle.focus();
  }

  function onKeydown(evt) {
    if (!isOpen()) return;

    if (evt.key === 'Escape' || evt.key === 'Esc') {
      evt.preventDefault();
      close(true);
      return;
    }

    if (evt.key !== 'Tab') return;

    var items = focusables();
    if (!items.length) { evt.preventDefault(); return; }
    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;

    if (!drawer.contains(active)) {
      evt.preventDefault();
      (evt.shiftKey ? last : first).focus();
      return;
    }
    if (evt.shiftKey && active === first) {
      evt.preventDefault();
      last.focus();
    } else if (!evt.shiftKey && active === last) {
      evt.preventDefault();
      first.focus();
    }
  }

  function init() {
    toggle = document.querySelector('[data-ps-navtoggle]');
    drawer = document.getElementById('ps-navdrawer');
    if (!toggle || !drawer) return;
    if (drawer.getAttribute('data-ps-nav-ready') === '1') return;
    drawer.setAttribute('data-ps-nav-ready', '1');

    overlay = document.querySelector('[data-ps-navoverlay]');
    closeBtn = drawer.querySelector('[data-ps-navclose]');

    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-haspopup', 'dialog');
    if (!toggle.getAttribute('aria-label')) toggle.setAttribute('aria-label', 'Open menu');

    toggle.addEventListener('click', function (evt) {
      evt.preventDefault();
      if (isOpen()) close(true); else open();
    });

    if (closeBtn) closeBtn.addEventListener('click', function () { close(true); });
    if (overlay) overlay.addEventListener('click', function () { close(true); });

    /* a link inside the drawer navigates — let it, but shut the panel so an
       in-page anchor (#ps-bundle, #ps-preorder) is visible when it lands */
    drawer.addEventListener('click', function (evt) {
      var a = evt.target.closest ? evt.target.closest('a[href]') : null;
      if (a && drawer.contains(a)) close(false);
    });

    document.addEventListener('keydown', onKeydown);

    window.addEventListener('resize', function () {
      if (isOpen() && window.innerWidth >= DESKTOP) close(false);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
